import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations } from '../data/translations';

type Language = 'en' | 'ar';
type Translations = typeof translations.en;

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: string) => any;
    dir: 'ltr' | 'rtl';
    isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [language, setLanguage] = useState<Language>('en');
    const [isLoading, setIsLoading] = useState(false);

    // Persist language preference
    useEffect(() => {
        const savedLang = localStorage.getItem('naja-lang') as Language;
        if (savedLang) setLanguage(savedLang);
    }, []);

    const handleSetLanguage = async (lang: Language) => {
        setIsLoading(true);
        // Minimum loading time for smooth transition
        await new Promise(resolve => setTimeout(resolve, 800));

        setLanguage(lang);
        localStorage.setItem('naja-lang', lang);
        // Update document direction for global layout support
        document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = lang;

        setIsLoading(false);
    };

    const t = (path: string) => {
        const keys = path.split('.');
        let current: any = translations[language];

        for (const key of keys) {
            if (current[key] === undefined) {
                console.warn(`Translation missing for key: ${path}`);
                return path;
            }
            current = current[key];
        }
        return current;
    };

    return (
        <LanguageContext.Provider value={{
            setLanguage: handleSetLanguage,
            t,
            dir: language === 'ar' ? 'rtl' : 'ltr',
            isLoading
        }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
